import { useEffect, useMemo, useState } from "react";
import { useGame } from "@/context/GameContext";
import { api, formatApiError } from "@/lib/api";
import { ITEM_ICON, SKILL_LABEL, skillIcon } from "@/lib/gameData";
import { toast } from "sonner";
import { EquipmentDoll } from "@/components/game/EquipmentDoll";
import { flagEmoji, flagUrl } from "@/lib/utils";
import {
  Coins, Heart, Home, PawPrint, ChevronRight, Hand, ShoppingCart, Send,
  Trophy, UserPlus, Store, Building2, Sparkles, ScrollText, Flag, Ban,
  ChevronDown, Pencil,
} from "lucide-react";

const ALL_SKILLS = [
  "attack", "strength", "defence", "hitpoints", "ranged", "prayer", "magic",
  "woodcutting", "fishing", "cooking", "firemaking",
  "mining", "smithing", "crafting", "fletching",
  "herblore", "runecrafting", "agility", "thieving", "slayer",
  "farming", "hunter", "construction",
];

function CountryFlag({ iso2, size = 80, className = "", title }) {
  if (!iso2) return null;
  const url = flagUrl(iso2, size);
  return (
    <img
      src={url}
      alt={iso2}
      title={title}
      loading="lazy"
      className={className}
      onError={(e) => { e.currentTarget.style.display = "none"; }}
      data-testid={`profile-flag-${iso2.toLowerCase()}`}
    />
  );
}

function Card({ title, right, children, className = "", testid }) {
  return (
    <section className={"panel p-3 " + className} data-testid={testid}>
      {title && (
        <header className="flex items-center justify-between mb-2">
          <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500">{title}</h3>
          {right}
        </header>
      )}
      {children}
    </section>
  );
}

function StatRow({ label, value, level, max = 99 }) {
  const pct = Math.min(100, Math.round((level / max) * 100));
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="w-32 flex items-center justify-between text-sm">
        <span className="text-stone-200 font-body">{label}</span>
        {level != null && (
          <span className="text-[10px] font-numeric text-stone-500 bg-stone-900 border border-stone-700 rounded px-1.5 py-0.5">
            Lv. {level}
          </span>
        )}
      </div>
      <div className="flex-1 xp-bar-outer" style={{ height: 6 }}>
        <div className="xp-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      {value != null && (
        <span className="w-10 text-right text-xs font-numeric text-amber-300">{value}</span>
      )}
    </div>
  );
}

function SkillBadge({ skill, lv }) {
  const url = skillIcon(skill);
  return (
    <div className="panel-inset p-1.5 flex flex-col items-center gap-0.5" title={`${SKILL_LABEL[skill]} Lv ${lv}`}>
      <div className="text-[9px] font-numeric text-stone-500 uppercase tracking-wider">Lv. {lv}</div>
      {url ? (
        <img src={url} alt={SKILL_LABEL[skill]} className="w-8 h-8 rounded-full object-cover" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-stone-900 border border-stone-700 flex items-center justify-center text-[9px] font-numeric text-amber-400">
          {SKILL_LABEL[skill]?.slice(0, 3)}
        </div>
      )}
    </div>
  );
}

function XpBarChart({ perXp }) {
  const items = useMemo(() => {
    return Object.entries(perXp || {})
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [perXp]);
  const max = items.reduce((m, [, v]) => Math.max(m, v), 1);
  if (items.length === 0) {
    return <div className="text-stone-500 italic text-xs">Train a skill to fill this chart.</div>;
  }
  return (
    <div className="space-y-1.5">
      {items.map(([s, xp]) => (
        <div key={s} className="flex items-center gap-2 text-xs font-body">
          <span className="w-20 truncate text-stone-400">{SKILL_LABEL[s]}</span>
          <div className="flex-1 xp-bar-outer" style={{ height: 6 }}>
            <div className="xp-bar-fill" style={{ width: `${(xp / max) * 100}%` }} />
          </div>
          <span className="w-16 text-right font-numeric text-amber-300">{xp.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function TotalXpSpark({ total, weight = 32 }) {
  // Deterministic pseudo-week curve driven by total XP so the chart still moves as you play.
  const pts = [];
  const w = 220;
  const h = 40;
  const seed = Math.max(1, total || 1);
  for (let i = 0; i < 7; i++) {
    const t = (i + 1) / 7;
    // hash-ish
    const wobble = Math.sin(seed * 0.0007 + i * 1.3) * 0.5 + 0.5;
    const y = h - (0.35 + 0.55 * t * (wobble + 0.5)) * h;
    pts.push(`${(i * (w / 6)).toFixed(1)},${y.toFixed(1)}`);
  }
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="none" data-testid="profile-activity-chart">
      <polyline fill="none" stroke="#f59e0b" strokeWidth={weight === 32 ? 1.5 : 2} points={pts.join(" ")} />
    </svg>
  );
}

// -- Secondary stats derived from character data
function derivedStats(profile, snap) {
  const eq = profile.equipment || {};
  const weapon = eq.weapon;
  const items = snap?.__contentItems || {};   // populated via caller if desired
  const atk_lv = snap?.skills?.attack?.level ?? 1;
  const str_lv = snap?.skills?.strength?.level ?? 1;
  const def_lv = snap?.skills?.defence?.level ?? 1;
  const rng_lv = snap?.skills?.ranged?.level ?? 1;
  const agi_lv = snap?.skills?.agility?.level ?? 1;
  return [
    { key: "critical_chance", label: "Critical Chance",
      desc: "Chance to land a critical hit.",
      value: `${(2 + agi_lv * 0.1).toFixed(1)}%` },
    { key: "critical_damage", label: "Critical Damage",
      desc: "Bonus damage when landing a critical hit.",
      value: `+${25 + str_lv}%` },
    { key: "attack_power", label: "Attack Power",
      desc: "Tone attack power from skills + equipment.",
      value: `${atk_lv + str_lv + (weapon ? 10 : 0)}` },
    { key: "protection", label: "Protection",
      desc: "Total protection gained from skills + equipment.",
      value: `${def_lv + 5}` },
    { key: "agility", label: "Agility",
      desc: "Affects your ability to evade enemy hits.",
      value: `${agi_lv}` },
    { key: "accuracy", label: "Accuracy",
      desc: "Chance of landing a successful hit.",
      value: `${60 + atk_lv}%` },
    { key: "movement_speed", label: "Movement Speed",
      desc: "Improves travel and hunting speed.",
      value: `${100 + agi_lv * 2}%` },
    { key: "damage", label: "Damage",
      desc: "Bonus damage applied to attack maximum.",
      value: `+${Math.max(1, Math.floor(str_lv / 2))}` },
  ];
}

export function ProfilePanel() {
  const { snap, content, startCombat } = useGame();
  const [profile, setProfile] = useState(null);
  const [bio, setBio] = useState("");
  const [editing, setEditing] = useState(false);
  const [showSecondary, setShowSecondary] = useState(true);

  const load = () => api.get("/api/game/profile").then(({ data }) => { setProfile(data); setBio(data.bio || ""); }).catch(() => {});
  useEffect(() => { load(); }, [snap?.name, snap?.gold, snap?.current_hp]);

  if (!profile) return <div className="p-6 text-stone-500 italic">Loading profile…</div>;

  const p = profile;
  const hpPct = p.max_hp ? (p.current_hp / p.max_hp) * 100 : 100;
  const activePet = p.pets?.active ? content?.pets?.[p.pets.active] : null;
  const themeImg = p.theme ? `/generated/theme_${p.theme}.png` : "/generated/hero_landing.png";
  const secondary = derivedStats(p, snap);

  async function saveBio() {
    try { await api.post("/api/game/profile/bio", { bio }); setEditing(false); toast.success("Bio saved."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="p-4 lg:p-6 space-y-3" data-testid="profile-panel">
      {/* Header with tabs */}
      <div className="flex items-center gap-2 text-xs font-body px-1">
        <button className="text-amber-400 border-b-2 border-amber-500 pb-1 px-1" data-testid="tab-profile">Profile</button>
        <button className="text-stone-500 hover:text-amber-500 pb-1 px-1" title="Coming soon">Skins</button>
        <button className="text-stone-500 hover:text-amber-500 pb-1 px-1" title="Coming soon">Badges</button>
      </div>

      {/* Portrait band */}
      <div
        className="panel overflow-hidden relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.4), rgba(12,10,9,0.92)), url(${themeImg})`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="p-4 sm:p-6 flex items-center gap-3 sm:gap-6">
          <div className="w-16 h-16 sm:w-24 sm:h-24 shrink-0 rounded-lg border-2 border-amber-700 bg-stone-950 flex items-center justify-center font-display text-2xl sm:text-4xl text-amber-400">
            {p.name.slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <h1 className="font-display text-2xl sm:text-4xl heading-gilded truncate" data-testid="profile-name">{p.name}</h1>
              {snap?.home_country_iso_a2 && (
                <span
                  className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-stone-950/70 border border-amber-800/60 backdrop-blur-sm"
                  data-testid="profile-header-flag"
                  title={snap.home_country_name || snap.home_country_iso}
                >
                  <CountryFlag
                    iso2={snap.home_country_iso_a2}
                    size={40}
                    className="w-6 h-4 object-cover rounded-sm shadow-[0_0_0_1px_rgba(0,0,0,0.6)]"
                  />
                  <span className="text-[11px] font-numeric uppercase tracking-widest text-amber-200">
                    {snap.home_country_iso}
                  </span>
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs sm:text-sm font-numeric text-stone-300">
              <span>Combat Lv. <span className="text-amber-300">{p.combat_level}</span></span>
              <span>Total Lv. <span className="text-amber-300">{p.total_level}</span></span>
              {snap?.home_country_name && (
                <span className="text-stone-400">
                  Homeland · <span className="text-amber-300">{snap.home_country_name}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Left column */}
        <div className="lg:col-span-3 space-y-3">
          <Card title="Actions" testid="actions-card">
            <div className="grid grid-cols-3 gap-1.5">
              <button onClick={() => toast.message("👋 You wave to yourself.")} className="btn-ghost-gold text-xs py-1 flex items-center gap-1 justify-center" data-testid="action-wave">
                <Hand size={12} /> Wave
              </button>
              <button onClick={() => toast.message("Open the Grand Exchange to trade.")} className="btn-ghost-gold text-xs py-1 flex items-center gap-1 justify-center" data-testid="action-trade">
                <ShoppingCart size={12} /> Trade
              </button>
              <button onClick={() => toast.message("World Chat available in the Play menu.")} className="btn-ghost-gold text-xs py-1 flex items-center gap-1 justify-center" data-testid="action-message">
                <Send size={12} /> Message
              </button>
            </div>
          </Card>

          <Card testid="no-action-card" className="text-center py-4">
            <div className="text-xs font-numeric uppercase tracking-widest text-stone-400 mb-1">{p.status}</div>
            <div className="text-[11px] text-stone-500 italic">
              {p.status === "Idle" ? "This character is not doing an action right now." :
                p.status === "In combat" ? "Currently engaging a monster." :
                "Currently training a skill."}
            </div>
          </Card>

          <Card title="More" testid="more-menu">
            <ul className="text-sm space-y-0.5 font-body">
              {[
                { label: "Metrics",         icon: Trophy },
                { label: "Add Friend",      icon: UserPlus, disabled: true },
                { label: "Market",          icon: Store },
                { label: "Museum",          icon: Building2 },
                { label: "House",           icon: Home },
                { label: "Active Effects",  icon: Sparkles, badge: (activePet ? 1 : 0) },
                { label: "Guidance Scroll", icon: ScrollText },
                { label: "Report",          icon: Flag, disabled: true },
                { label: "Block",           icon: Ban,  disabled: true },
              ].map((m) => {
                const Icon = m.icon;
                return (
                  <li key={m.label}>
                    <button
                      disabled={m.disabled}
                      className={
                        "w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors duration-150 " +
                        (m.disabled ? "text-stone-600 cursor-not-allowed" : "text-stone-300 hover:bg-stone-800/60 hover:text-amber-300")
                      }
                    >
                      <Icon size={14} className="opacity-70" />
                      <span className="flex-1">{m.label}</span>
                      {m.badge > 0 && (
                        <span className="text-[9px] font-numeric text-amber-300 bg-amber-900/40 border border-amber-700 rounded-full w-5 h-5 flex items-center justify-center">{m.badge}</span>
                      )}
                      <ChevronRight size={12} className="text-stone-600" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>

        {/* Center column */}
        <div className="lg:col-span-6 space-y-3">
          <Card title="Bio" right={
            <button onClick={() => setEditing((v) => !v)} className="text-stone-500 hover:text-amber-400" data-testid="bio-edit">
              <Pencil size={12} />
            </button>
          } testid="bio-card">
            {editing ? (
              <div className="space-y-2">
                <textarea
                  value={bio}
                  maxLength={280}
                  onChange={(e) => setBio(e.target.value)}
                  className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1 text-sm font-body min-h-[72px]"
                  data-testid="bio-input"
                />
                <div className="flex justify-end gap-2">
                  <button onClick={() => { setBio(p.bio); setEditing(false); }} className="btn-ghost-gold text-xs px-3 py-1">Cancel</button>
                  <button onClick={saveBio} className="btn-gold text-xs px-3 py-1" data-testid="bio-save">Save</button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-stone-300 font-body italic">{p.bio}</p>
            )}
          </Card>
          <Card title="Activity" right={<span className="text-[10px] text-stone-500 font-numeric">7 days</span>} testid="activity-card">
            <TotalXpSpark total={p.total_xp} />
          </Card>
          <Card title="Experience" testid="experience-card">
            <XpBarChart perXp={p.per_skill_xp} />
          </Card>
        </div>

        {/* Right column */}
        <div className="lg:col-span-3 space-y-3">
          <Card title="Equipment" testid="equipment-card">
            <EquipmentDoll compact showStats />
          </Card>
          <Card title="Skills" testid="skills-grid-card">
            <div className="grid grid-cols-4 gap-1.5">
              {ALL_SKILLS.map((s) => (
                <SkillBadge key={s} skill={s} lv={snap?.skills?.[s]?.level ?? 1} />
              ))}
            </div>
          </Card>
        </div>

        {/* Full width: Health */}
        <div className="lg:col-span-12">
          <Card title="Health" testid="health-card">
            <div className="flex items-center gap-3">
              <Heart size={14} className="fill-red-500 text-red-500" />
              <div className="flex-1 hp-bar-outer">
                <div className="hp-bar-fill" style={{ width: `${hpPct}%` }} />
              </div>
              <span className="text-xs font-numeric text-red-300">{p.current_hp} / {p.max_hp}</span>
            </div>
          </Card>
        </div>

        {/* Information + Primary stats */}
        <div className="lg:col-span-4 space-y-3">
          <Card title="Information" testid="info-card">
            <dl className="text-sm font-body space-y-1.5">
              <div className="flex justify-between"><dt className="text-stone-500">Status</dt><dd className="text-emerald-300">{p.status}</dd></div>
              <div className="flex justify-between"><dt className="text-stone-500">Gold</dt><dd className="text-amber-300 font-numeric flex items-center gap-1"><Coins size={12} />{p.gold.toLocaleString()}</dd></div>
              <div className="flex justify-between"><dt className="text-stone-500">Bank</dt><dd className="text-amber-300 font-numeric">{p.bank_stacks} stacks</dd></div>
              {snap?.home_country_iso && (
                <div className="flex justify-between items-center" data-testid="profile-homeland">
                  <dt className="text-stone-500">Homeland</dt>
                  <dd className="text-amber-300 flex items-center gap-2">
                    {snap.home_country_iso_a2 ? (
                      <CountryFlag
                        iso2={snap.home_country_iso_a2}
                        size={40}
                        className="w-5 h-3.5 object-cover rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.6)]"
                      />
                    ) : (
                      <span className="text-base leading-none" aria-hidden>
                        {flagEmoji(snap.home_country_iso_a2)}
                      </span>
                    )}
                    <span>{snap.home_country_name || snap.home_country_iso}</span>
                    <span className="text-[10px] font-numeric text-stone-500">{snap.home_country_iso}</span>
                  </dd>
                </div>
              )}
              <div className="flex justify-between"><dt className="text-stone-500">Birth date</dt><dd className="text-amber-300">{p.created_at ? new Date(p.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—"}</dd></div>
            </dl>
          </Card>
        </div>
        <div className="lg:col-span-8">
          <Card title="Primary Stats" testid="primary-stats">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <StatRow label="Strength" level={snap?.skills?.strength?.level ?? 1} />
              <StatRow label="Defence"  level={snap?.skills?.defence?.level ?? 1} />
              <StatRow label="Ranged"   level={snap?.skills?.ranged?.level ?? 1} />
              <StatRow label="Dexterity" level={snap?.skills?.agility?.level ?? 1} />
              <StatRow label="Magic"    level={snap?.skills?.magic?.level ?? 1} />
              <StatRow label="Prayer"   level={snap?.skills?.prayer?.level ?? 1} />
            </div>
          </Card>
        </div>

        {/* Secondary stats */}
        <div className="lg:col-span-12">
          <Card title="Secondary Stats" testid="secondary-stats" right={
            <button onClick={() => setShowSecondary((v) => !v)} className="text-stone-500 hover:text-amber-400">
              <ChevronDown size={12} className={showSecondary ? "" : "-rotate-90"} />
            </button>
          }>
            {showSecondary && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                {secondary.map((s) => (
                  <div key={s.key} className="flex items-start gap-3 py-2 border-b border-stone-800 last:border-none">
                    <div className="w-8 h-8 rounded-full bg-amber-900/30 border border-amber-700 flex items-center justify-center text-[10px] font-numeric text-amber-400">
                      %
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-body text-stone-200">{s.label}</div>
                      <div className="text-[11px] text-stone-500 italic">{s.desc}</div>
                    </div>
                    <div className="font-numeric text-amber-300 text-sm">{s.value}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Pet card */}
        <div className="lg:col-span-12">
          <Card title="Pet" testid="pet-card">
            {activePet ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col items-center text-center gap-2">
                  <img src={`/generated/pet_${p.pets.active}.png`} alt="" className="w-24 h-24 rounded-full border-2 border-amber-700 object-cover" />
                  <div className="font-display text-xl heading-gilded">{activePet.name}</div>
                  <div className="text-[10px] font-numeric text-stone-500 uppercase tracking-widest">Level 1</div>
                </div>
                <div className="md:col-span-2 grid grid-cols-2 gap-2 self-center">
                  <PetStat label="Agility"        val="0" />
                  <PetStat label="Accuracy"       val="0" />
                  <PetStat label="Protection"     val="0" />
                  <PetStat label="Attack Power"   val="0" />
                  <PetStat label="Movement Speed" val="0" />
                  <PetStat label="Critical Chance" val="0" />
                  <PetStat label="Critical Damage" val="0" />
                  <PetStat label="XP boost"        val={`+${Math.round(activePet.buff * 100)}%`} highlight />
                </div>
              </div>
            ) : (
              <div className="text-stone-500 italic text-sm font-body text-center py-4">
                No companion active. Unlock a pet from any skill to begin.
              </div>
            )}
            <div className="mt-3 flex justify-end">
              <a href="#" onClick={(e) => e.preventDefault()} className="text-xs font-body text-stone-500 hover:text-amber-400" data-testid="all-pets-link">
                All Pets →
              </a>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function PetStat({ label, val, highlight }) {
  return (
    <div className={"panel-inset px-3 py-2 flex items-center justify-between text-xs font-body " + (highlight ? "border-emerald-700" : "")}>
      <span className="text-stone-400">{label}</span>
      <span className={"font-numeric " + (highlight ? "text-emerald-300" : "text-amber-300")}>{val}</span>
    </div>
  );
}
